import React, {
  useState, useCallback, useEffect, useMemo, useRef,
} from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../../../redux/actions';
import { formatEmployeeThaiName } from '../../../../utils/functions/employeeName';
import useMarketingLiveRefresh from '../../hooks/useMarketingLiveRefresh';
import ModalHeader from '../ui/ModalHeader';
import { getStatusHex } from '../../constants/status';

// สีของ event วันที่นัด — คงที่จุดเดียว ไม่อิงสถานะ (ต่างจาก event วันที่เสนอราคาที่ไล่สีตาม
// getStatusHex(row.status) — สีจริงต่อสถานะ hex เดียวกับที่ใช้ทั้งโมดูล ดู constants/status.js)
// ให้แยกออกจากกันง่ายๆ ด้วยตาว่าจุดไหนคือวันเสนอราคา จุดไหนคือวันนัด
const APPOINTMENT_EVENT_COLOR = '#6366f1';

// ป้ายวงเล็บต่อท้าย "เสนอราคา" บน event card ในปฏิทิน — โชว์เฉพาะ event ประเภทเสนอราคา (ไม่ใช่วัน
// นัด) ให้เห็นสถานะดีลปัจจุบันโดยไม่ต้องคลิกเข้าไปดู ข้อความ/สีอ้างอิงสถานะเดียวกับ getStatusHex
const QUOTATION_STATUS_SUFFIX = {
  WAITING: ' (ยังไม่ได้เริ่ม)',
  IN_PROGRESS: ' (กำลังทำ)',
  ON_HOLD: ' (รอลูกค้า)',
  WON: ' (ได้งานแล้ว)',
  LOST: ' (ไม่ได้งาน)',
};

// ปฏิทินโอกาสทางธุรกิจ — แต่ละ event โชว์ 3 บรรทัดเต็ม (ชื่อวันนัด/เสนอราคา, ชื่อโครงการ, ชื่อ
// ลูกค้า) ผ่าน eventContent ที่คุมเอง ไม่ใช้ title เดียวบรรทัดเดียวแบบ default ของ FullCalendar
// (โดน ellipsis ตัดทิ้งถ้าแคบ) — ชื่อวันนัดคือ title ที่ผู้ใช้ตั้งเองตอนเพิ่มวันนัด (คอลัมน์
// "วันที่นัด" ในตาราง) ถ้าไม่ได้ตั้งชื่อไว้ fallback เป็น "นัดหมาย" — dayMaxEvents={false} กันวันที่
// มีหลาย event ถูกยุบเป็น "+N more" ให้เห็นครบทุกรายการเสมอ — ความกว้าง modal เท่าเดิม
// (maxWidth="lg") ไม่ทำ fullScreen ตามที่ user ขอ แต่เพิ่มเฉพาะความสูง (PaperProps height:
// '94vh') ให้มีที่พอสำหรับ event หลายบรรทัด ร่วมกับ FullCalendar height="auto" (ไม่ใช่ vh คงที่)
// ปล่อยให้แต่ละแถวสัปดาห์สูงเท่าที่เนื้อหาต้องการจริง ไม่มี scroll ซ้อนในตัวปฏิทินเอง — ถ้าเดือน
// ไหน event เยอะผิดปกติจนสูงเกิน 94vh จริงๆ DialogContent จะ scroll ทั้งก้อนแทน (single scroll)
// ไม่ใช่ scroll ซ้อนสองชั้น ดึงข้อมูลเฉพาะช่วงเดือน
// ที่กำลังมองเห็นบนปฏิทิน (FullCalendar ยิง datesSet ให้ทั้งตอน mount ครั้งแรกและทุกครั้งที่
// เปลี่ยนเดือน) ไม่ได้โหลดทั้งหมดทีเดียวเหมือน Profile/Calendar.js เดิม เพราะจำนวนโอกาสทางธุรกิจ
// สะสมได้เยอะกว่าโปรเจกต์มาก — ใช้ includeAppointmentDates:true (ดู opportunity.repository.js
// ฝั่ง services/marketing) ให้ backend ดึงแถวที่ "วันที่เสนอราคา" หรือ "วันที่นัด" ตกในช่วงนี้ก็ได้
// ไม่ใช่กรองแค่ quotation_due_date เหมือน dueDateFrom/dueDateTo ปกติของหน้า list
export function OpportunityCalendarModal({
  open,
  onClose,
  onOpportunityClick,
  ownerId,
  title = 'ปฏิทินวันที่เสนอราคา / วันที่นัด',
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  // owner_id ไม่มี name snapshot เก็บไว้ในแถว opportunity (ต่างจาก contact_name_snapshot) ต้อง
  // resolve ผ่านรายชื่อพนักงานแยกเหมือน ownerNameById ใน OpportunityList.js — ดึงครั้งเดียวตอน
  // modal เปิด ไม่ผูกกับ loadRange (เดือนเปลี่ยนไม่ต้อง fetch พนักงานซ้ำ)
  const employee = useSelector((state) => state.employee);
  useEffect(() => {
    if (open) dispatch(actions.employeeAll({ size: 9999, activeOnly: false }));
  }, [dispatch, open]);
  const ownerNameById = useMemo(() => {
    const map = {};
    (employee?.rows || []).forEach((e) => {
      map[e._id || e.id] = formatEmployeeThaiName(e);
    });
    return map;
  }, [employee?.rows]);

  const loadRange = useCallback(
    async (startStr, endStr, silent = false) => {
      if (!silent) setLoading(true);
      try {
        const data = await dispatch(
          actions.listOpportunities({
            silent,
            quotationDueDateFrom: startStr,
            quotationDueDateTo: endStr,
            includeAppointmentDates: true,
            ownerId: ownerId || undefined,
            size: 500,
            sortBy: 'quotation_due_date',
            sortOrder: 'asc',
          }),
        );
        const nextEvents = [];
        (data.data || []).forEach((row) => {
          // งานที่ยกเลิกแล้วไม่มีความหมายให้ติดตามวันที่เสนอราคา/วันนัดอีกต่อไป — ไม่โชว์ทั้ง
          // สอง event type ของโปรเจกต์นี้เลย
          if (row.status === 'CANCELLED') return;
          // เก็บ owner_id ดิบไว้ ไม่ resolve เป็นชื่อตรงนี้ — employeeAll กับ loadRange ยิงคนละ
          // จังหวะกัน (employeeAll อาจ resolve ทีหลัง events ชุดแรก) ถ้า bake ownerName เข้า
          // extendedProps ตอนนี้เลยอาจได้ชื่อว่างค้างจนกว่าจะเปลี่ยนเดือน — resolve ตอน render
          // (eventContent) แทน ที่ ownerNameById เป็นค่าล่าสุดเสมอ
          if (row.quotation_due_date) {
            nextEvents.push({
              id: `q-${row.id}`,
              title: `เสนอราคา: ${row.project_name}`,
              start: row.quotation_due_date,
              allDay: true,
              backgroundColor: getStatusHex(row.status),
              borderColor: getStatusHex(row.status),
              extendedProps: {
                opportunityId: row.id,
                label: 'เสนอราคา',
                status: row.status,
                projectName: row.project_name,
                contactName: row.contact_name_snapshot,
                ownerId: row.owner_id,
              },
            });
          }
          (row.marketing_appointments || []).forEach((appt) => {
            nextEvents.push({
              id: `a-${appt.id}`,
              title: `${appt.title || 'นัดหมาย'}: ${row.project_name}`,
              start: appt.date,
              allDay: true,
              backgroundColor: APPOINTMENT_EVENT_COLOR,
              borderColor: APPOINTMENT_EVENT_COLOR,
              extendedProps: {
                opportunityId: row.id,
                label: appt.title || 'นัดหมาย',
                projectName: row.project_name,
                contactName: row.contact_name_snapshot,
                ownerId: row.owner_id,
              },
            });
          });
        });
        setEvents((prev) => (JSON.stringify(prev) === JSON.stringify(nextEvents) ? prev : nextEvents));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [dispatch, ownerId],
  );

  // เดือนที่กำลังดูอยู่ (จาก datesSet) — ใช้โหลดช่วงเดิมซ้ำตอนมีการแก้โอกาสทางธุรกิจจากที่อื่น (popup
  // รายละเอียดที่เปิดทับปฏิทิน, หน้าต่าง pop-out, แท็บอื่น) ให้สี/ป้ายสถานะ/วันที่อัปเดตทันทีไม่ต้อง refresh
  const rangeRef = useRef(null);
  // รวม BroadcastChannel (แก้ในเบราว์เซอร์เดียวกัน) + focus + interval เงียบๆ (ผู้ใช้/เครื่องอื่น)
  useMarketingLiveRefresh(() => {
    if (rangeRef.current) return loadRange(rangeRef.current.start, rangeRef.current.end, true);
    return undefined;
  }, { enabled: open });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, height: '94vh', maxHeight: '94vh' },
      }}
    >
      <ModalHeader
        icon={<EventOutlinedIcon fontSize="small" />}
        title={title}
        extra={loading ? <CircularProgress size={16} /> : null}
        onClose={onClose}
      />
      <DialogContent sx={{ pt: 2 }}>
        <Box
          sx={{
            '& .fc-event': { cursor: 'pointer' },
            // ปกติ FullCalendar ตัดข้อความด้วย ellipsis บรรทัดเดียวต่อ event (คับแคบเกินจะโชว์
            // ชื่อวันนัด/โปรเจกต์/ลูกค้าครบ) — เปิด white-space:normal ให้ตัวข้อความใน
            // eventContent ที่เราคุมเอง (ด้านล่าง) ขึ้นบรรทัดใหม่ได้แทนโดนตัดทิ้ง
            '& .fc-daygrid-event': {
              whiteSpace: 'normal',
              alignItems: 'flex-start',
            },
            '& .fc-daygrid-day-frame': { minHeight: 90 },
            // วันปัจจุบัน — เอาพื้นหลังสีเหลืองจางๆ default ของ FullCalendar ออก แล้วใส่วงกลม
            // สีตรงเลขวันที่แทน (โทนเดียวกับปุ่ม primary ของแอป) ให้เด่นแบบเดียวกับปฏิทินทั่วไป
            '& .fc-day-today': { backgroundColor: 'transparent !important' },
            '& .fc-day-today .fc-daygrid-day-number': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: 700,
            },
          }}
        >
          <FullCalendar
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            height="auto"
            dayMaxEvents={false}
            events={events}
            datesSet={(info) => {
              rangeRef.current = { start: info.startStr, end: info.endStr };
              loadRange(info.startStr, info.endStr);
            }}
            eventContent={(arg) => {
              const {
                label,
                status,
                projectName,
                contactName,
                ownerId,
              } = arg.event.extendedProps;
              const suffix = status ? (QUOTATION_STATUS_SUFFIX[status] || '') : '';
              const ownerName = ownerId ? (ownerNameById[ownerId] || '') : '';
              return (
                <Box sx={{ px: 0.5, py: 0.25, overflow: 'hidden' }}>
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      lineHeight: 1.3,
                    }}
                  >
                    {label}
                    {suffix}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.3 }}>
                    {projectName}
                  </Typography>
                  {contactName && (
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        lineHeight: 1.3,
                        opacity: 0.85,
                      }}
                    >
                      {contactName}
                    </Typography>
                  )}
                  {ownerName && (
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        lineHeight: 1.3,
                        opacity: 0.85,
                        fontStyle: 'italic',
                      }}
                    >
                      ผู้รับผิดชอบ: {ownerName}
                    </Typography>
                  )}
                </Box>
              );
            }}
            eventClick={(clickInfo) => {
              clickInfo.jsEvent.preventDefault();
              onOpportunityClick?.(clickInfo.event.extendedProps.opportunityId);
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default OpportunityCalendarModal;
